import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum } from "~/types/enums";
import { Alert, Badge, Box, Button, Card, Grid, Icon, InputCheckbox, InputText, InputToggle, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import confluenceLogo from "~/assets/i/apps/atlassian_confluence_logo.svg";
import styled from "styled-components";
import Markdown from "~/components/Markdown";
import { handleError, helpers } from "~/lib/helpers";
import { useTranslation } from "react-i18next";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const StyledGrid = styled(Grid)`
  position: relative;
  z-index: 2;
  max-height: 250px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const authorities = [AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE, AuthoritiesEnum.SIGN_UP_INTEGRATE];
const ConfluenceIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    userHasOneOf
  } = useAbacExt();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const integration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.CONFLUENCE));
  const [spaces, setSpaces] = useState([]);
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [spacesError, setSpacesError] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const [spacesSearch, setSpacesSearch] = useState("");
  const [selectedType, setSelectedType] = React.useState("all");
  const byType = helpers.groupByCount(spaces, "type");
  const mappedTypes = {
    global: "Global",
    collaboration: "Collaboration",
    knowledge_base: "Knowledge base",
    personal: "Personal"
  };
  const handleLoadConfluenceSpaces = () => {
    setLoadingSpaces(true);
    setSpacesError(undefined);
    api.fetchConfluenceSpaces().then(data => setSpaces(data)).catch(e => setSpacesError(handleError(e))).finally(() => setLoadingSpaces(false));
  };
  useEffect(() => {
    if (integration && userHasOneOf(authorities)) {
      handleLoadConfluenceSpaces();
    }
  }, [!!integration]);
  const handleToggle = value => {
    if (value) {
      api.confluenceInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Confluence", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
    } else if (integration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(integration.id));
    }
  };
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  const handleUpdateSpaces = function (spaceIds) {
    let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    let deselect = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (integration) {
      let spacesList = integration.settings?.spaces || [];
      spaceIds.forEach(spaceId => {
        if (spacesList.includes(spaceId) && !force || deselect && force) {
          spacesList = spacesList.filter(id => id !== spaceId);
        } else {
          spacesList.push(spaceId);
        }
      });
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          spaces: spacesList
        }
      }));
    }
  };
  const handleSearchSpaces = value => {
    setSpacesSearch(value);
  };
  const filteredSpaces = spaces.filter(space => space.name.toLowerCase().includes(spacesSearch.toLowerCase()) || space.key.toLowerCase().includes(spacesSearch.toLowerCase())).filter(space => selectedType === "all" || selectedType === space.type);
  const hasFiltered = filteredSpaces.length && spacesSearch;
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf(authorities) && integration ? handleExpand : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: confluenceLogo,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Atlassian Confluence"), integration && userHasOneOf(authorities) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      value: !!integration,
      display: "inline-block",
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf(authorities)
    })),
    collapsed: !integration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), !!integration && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Markdown, {
    display: "block",
    fontSize: 4,
    lineHeight: 6,
    linkColor: "primary",
    s: {
      mb: 3
    },
    text: `Connected to [${integration?.settings?.cloud[0]?.name}](${integration?.settings?.cloud[0]?.url}) account. Select Space(s) you want us to subscribe and map.`
  }), /*#__PURE__*/React.createElement(InputText, {
    onClick: e => e.stopPropagation(),
    s: {
      mb: 4
    },
    value: spacesSearch,
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "search",
      s: {
        mr: 2
      }
    }),
    placeholder: t("common.actions.search"),
    onChange: val => handleSearchSpaces(val),
    elemAfter: !!spacesSearch && /*#__PURE__*/React.createElement(Box, {
      gap: 1,
      s: {
        ml: 1
      }
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdateSpaces(filteredSpaces.map(r => r.id), true);
      },
      color: "translucent",
      outline: false,
      fontSize: 3,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Select found" : "Select all"), /*#__PURE__*/React.createElement(Button, {
      onClick: e => {
        e.stopPropagation();
        handleUpdateSpaces(filteredSpaces.map(r => r.id), true, true);
      },
      color: "translucent",
      fontSize: 3,
      outline: false,
      s: {
        px: 3,
        py: 1
      },
      rounded: true
    }, hasFiltered ? "Deselect found" : "Deselect all")),
    clearButton: true,
    fullWidth: true
  }), spacesError && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, spacesError), /*#__PURE__*/React.createElement(Box, {
    gap: 2,
    s: {
      mb: 3
    }
  }, /*#__PURE__*/React.createElement(Button, {
    fontSize: 3.4,
    s: {
      py: 1,
      pr: 1,
      pl: 3
    },
    color: selectedType === "all" ? "translucentPrimary" : "translucent",
    onClick: e => {
      e.stopPropagation();
      setSelectedType("all");
    },
    outline: false
  }, "All", /*#__PURE__*/React.createElement(Badge, {
    s: {
      py: 0.5,
      px: 2,
      ml: 1
    },
    color: "translucent"
  }, spaces.length)), Object.keys(byType).map(key => /*#__PURE__*/React.createElement(Button, {
    key: key,
    fontSize: 3.4,
    s: {
      py: 1,
      pr: 1,
      pl: 3
    },
    color: selectedType === key ? "translucentPrimary" : "translucent",
    onClick: e => {
      e.stopPropagation();
      setSelectedType(key);
    },
    outline: false
  }, mappedTypes[key], /*#__PURE__*/React.createElement(Badge, {
    s: {
      py: 0.5,
      px: 2,
      ml: 2
    },
    color: "translucent"
  }, byType[key])))), loadingSpaces ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : filteredSpaces.length ? /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 4,
    template: "1rem 1fr auto",
    s: {
      pl: 2,
      pr: 2
    },
    gap: 3,
    align: "center",
    fullWidth: true
  }, filteredSpaces.map(space => /*#__PURE__*/React.createElement(React.Fragment, {
    key: space.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: integration?.settings?.spaces?.includes(space.id),
    onChange: () => handleUpdateSpaces([space.id]),
    s: {
      mt: -2.5
    }
  }), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    fontSize: 4,
    s: {
      pt: 1
    },
    weight: "semiBold"
  }, space.name, " ", space.type === "global" ? `(${space.key})` : ""), /*#__PURE__*/React.createElement(Grid.Cell, {
    align: "center",
    justify: "end"
  }, /*#__PURE__*/React.createElement(Badge, {
    color: "translucentPrimary",
    s: {
      px: 2.5
    }
  }, mappedTypes[space.type]))))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold",
    s: {
      my: 2
    },
    align: "center"
  }, "No spaces found")));
};
export default ConfluenceIntegrationCard;